// /admin/v2/pyq — PYQ tags (BRIEFS C11.4). Per chapter: the questions tagged as past-paper
// questions (question id, tag, match score, start of the question) with "Remove tag"
// (the tag is switched off, never deleted). View and remove only: tags come from each
// chapter's pyq.json (and new PYQs from new_questions.json), loaded at startup.
import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { errText, v2AdminChapters, v2PyqList, v2PyqRemove } from '../../../api/v2admin';
import V2Tabs, { Msg, chapterLabel } from './V2Tabs';

function Row({ t, onDone }) {
  const [asking, setAsking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const remove = async () => {
    setBusy(true); setErr('');
    try {
      await v2PyqRemove(t.id);
      setAsking(false);
      onDone(`Tag "${t.label}" removed from Q ${t.question_id}.`);
    } catch (x) {
      setErr(errText(x, 'Could not remove the tag.'));
    } finally { setBusy(false); }
  };
  return (
    <tr data-testid={`v2pyq-row-${t.id}`} style={{ opacity: t.is_active ? 1 : 0.55 }}>
      <td style={{ verticalAlign: 'top', whiteSpace: 'nowrap' }}><code>Q {t.question_id}</code>
        {t.is_new && <div><span className="badge badge-blue">Added PYQ</span></div>}
      </td>
      <td style={{ verticalAlign: 'top', whiteSpace: 'nowrap' }}>
        <span className="badge badge-yellow">{t.label}</span>
        {!t.is_active && <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>Removed</div>}
      </td>
      <td style={{ verticalAlign: 'top', whiteSpace: 'nowrap' }}>
        {t.match_score == null ? '—' : `${Math.round(t.match_score * 100)}%`}
      </td>
      <td style={{ verticalAlign: 'top', minWidth: 280 }}>
        {t.question_start}
        {t.source_ref && <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>Source: {t.source_ref}</div>}
        {err && <div style={{ color: 'var(--danger)', fontSize: 13 }}>{err}</div>}
      </td>
      <td style={{ verticalAlign: 'top', whiteSpace: 'nowrap' }}>
        {t.is_active && !asking && (
          <button className="btn btn-ghost btn-sm" data-testid={`v2pyq-remove-${t.id}`}
            onClick={() => setAsking(true)}>Remove tag</button>
        )}
        {asking && (
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ fontSize: 13 }}>Remove this tag?</span>
            <button className="btn btn-danger btn-sm" disabled={busy} onClick={remove}
              data-testid={`v2pyq-yes-${t.id}`}>Yes, remove</button>
            <button className="btn btn-ghost btn-sm" disabled={busy} onClick={() => setAsking(false)}>No</button>
          </div>
        )}
      </td>
    </tr>
  );
}

export default function AdminV2Pyq() {
  const [params, setParams] = useSearchParams();
  const chapterId = params.get('chapter') || '';
  const [showRemoved, setShowRemoved] = useState(false);
  const [msg, setMsg] = useState(null);
  const chaptersQ = useQuery({ queryKey: ['v2a', 'chapters'], queryFn: v2AdminChapters });
  const listQ = useQuery({
    queryKey: ['v2a', 'pyq', chapterId, showRemoved],
    queryFn: () => v2PyqList({ chapter_id: chapterId || undefined, include_removed: showRemoved || undefined }),
  });
  const chapters = (chaptersQ.data && (chaptersQ.data.chapters || chaptersQ.data)) || [];
  const data = listQ.data || { tags: [], active: 0, questions: 0, removed: 0 };
  const done = (text) => { setMsg({ kind: 'ok', text }); listQ.refetch(); };

  return (
    <div>
      <V2Tabs title="PYQ tags" intro="Questions that came in NEET / AIPMT, with their exam and year. Harsh sees the tag on the question and can practise only PYQs. Tags come from each chapter's pyq.json; here you can only check them and remove a wrong tag." />
      <Msg msg={msg} />
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 14 }}>
        <select className="form-input" value={chapterId} data-testid="v2pyq-chapter"
          onChange={(e) => setParams(e.target.value ? { chapter: e.target.value } : {})} style={{ maxWidth: 420 }}>
          <option value="">All chapters</option>
          {Array.isArray(chapters) && chapters.map((c) => (
            <option key={c.chapter_id} value={c.chapter_id}>{c.class_level ? chapterLabel(c) : c.title}</option>
          ))}
        </select>
        <label style={{ fontSize: 14, display: 'flex', alignItems: 'center', gap: 6 }}>
          <input type="checkbox" checked={showRemoved} onChange={(e) => setShowRemoved(e.target.checked)}
            data-testid="v2pyq-show-removed" /> Show removed tags
        </label>
      </div>
      <div style={{ fontSize: 14, color: 'var(--text-muted)', marginBottom: 8 }} data-testid="v2pyq-count">
        {data.active} tags on {data.questions} questions · {data.removed} removed
      </div>
      {listQ.isLoading ? <div>Loading…</div> : listQ.isError ? (
        <Msg msg={{ kind: 'err', text: errText(listQ.error, 'Could not load the tags.') }} />
      ) : (
        <div className="card" style={{ padding: 0, overflowX: 'auto' }}>
          <table className="table" style={{ width: '100%' }}>
            <thead><tr><th>Question</th><th>Tag</th><th>Match</th><th>Question starts</th><th /></tr></thead>
            <tbody>
              {data.tags.map((t) => <Row key={t.id} t={t} onDone={done} />)}
              {!data.tags.length && <tr><td colSpan={5} style={{ color: 'var(--text-muted)' }}>No PYQ tags yet.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
